'use client'

import { useState } from 'react'
import { PawIcon } from './icons'

const BACKEND_URL = process.env.NEXT_PUBLIC_MEDUSA_BACKEND_URL || ''
const PUBLISHABLE_KEY = process.env.NEXT_PUBLIC_MEDUSA_PUBLISHABLE_KEY || ''

// Generate a random password for newsletter-only subscribers
function generatePassword() {
  return `NL${Date.now()}${Math.random().toString(36).slice(2, 10)}!`
}

export function PetSelectNewsletter() {
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')
  const [error, setError] = useState('')

  async function handleSubmit() {
    if (!email) return
    setLoading(true)
    setError('')
    setSuccess(false)

    try {
      // Step 1: Register auth identity
      const password = generatePassword()
      const authRes = await fetch(`${BACKEND_URL}/auth/customer/emailpass/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-publishable-api-key': PUBLISHABLE_KEY,
        },
        body: JSON.stringify({ email, password }),
      })

      if (authRes.status === 400 || authRes.status === 409) {
        // Already registered — that's fine for newsletter
        setSuccessMessage("You're already subscribed!")
        setSuccess(true)
        setEmail('')
        return
      }

      if (!authRes.ok) {
        const data = await authRes.json().catch(() => ({}))
        setError(data?.message || 'Something went wrong. Please try again.')
        return
      }

      const { token } = await authRes.json()

      // Step 2: Create customer profile with the auth token
      const customerRes = await fetch(`${BACKEND_URL}/store/customers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-publishable-api-key': PUBLISHABLE_KEY,
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          email,
          first_name: 'Newsletter',
          last_name: 'Subscriber',
          metadata: { newsletter: true, subscribed_at: new Date().toISOString() },
        }),
      })

      if (customerRes.ok || customerRes.status === 409) {
        setSuccessMessage("You're subscribed!")
        setSuccess(true)
        setEmail('')
      } else {
        // Auth succeeded but customer creation failed — still show success
        // since the auth identity exists
        setSuccessMessage("You're subscribed!")
        setSuccess(true)
        setEmail('')
      }
    } catch {
      setError('Something went wrong. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <section className="py-14 md:py-18 bg-gradient-to-br from-[#0B7A75] to-[#065A55]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <PawIcon className="w-8 h-8 text-white/40 mx-auto mb-4" />
        <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
          Join the PetSelect Family
        </h2>
        <p className="text-white/70 mb-6 text-sm max-w-md mx-auto">
          Get exclusive deals, pet care tips, and new product alerts straight to your inbox.
        </p>
        {success ? (
          <p className="text-white font-semibold text-sm">{successMessage}</p>
        ) : (
          <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input
              type="email"
              placeholder="Your email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
              disabled={loading}
              className="flex-1 px-5 py-2.5 rounded-full text-sm bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/30 backdrop-blur-sm disabled:opacity-50"
            />
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="px-6 py-2.5 bg-[#FF6B35] hover:bg-[#E55A2B] text-white font-semibold rounded-full text-sm transition-colors whitespace-nowrap disabled:opacity-50"
            >
              {loading ? 'Subscribing…' : 'Subscribe'}
            </button>
          </div>
        )}
        {error && <p className="text-red-300 text-xs mt-2">{error}</p>}
        <p className="text-white/40 text-[10px] mt-3">No spam. Unsubscribe anytime.</p>
      </div>
    </section>
  )
}
